<script setup lang="ts">
import type { Color } from '../../types/shared'
import type { ChipSize, ChipWeight } from './types'

type Props = {
  size?: ChipSize
  weight?: ChipWeight
  color?: Color
  active?: boolean
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  color: '',
  size: 'md',
  weight: 'extra-bold',
})

const emit = defineEmits(['click'])

const componentClasses = computed(() => {
  const classes = [
    'app-chip',
    `app-chip--size-${props.size}`,
    `app-chip--weight-${props.weight}`,
  ]

  if (props.disabled) classes.push('app-chip--disabled')

  if (props.active) classes.push('app-chip--active')

  return classes
})

const activeStyles = computed(() => {
  const styles: Partial<Record<string, string>> = {}

  if (props.color) styles['--backgroundColor'] = props.color

  return styles
})

const handleClick = (event: MouseEvent) => {
  if (!props.disabled) emit('click', event)
}

const handleKeydown = (event: KeyboardEvent) => {
  if (props.disabled || (event.key !== 'Enter' && event.key !== ' ')) {
    return
  }

  event.preventDefault()
  emit('click', event)
}
</script>

<template>
  <span
    v-bind="$attrs"
    :class="componentClasses"
    :style="activeStyles"
    role="button"
    tabindex="0"
    @click="handleClick"
    @keydown="handleKeydown"
  >
    <slot />
  </span>
</template>

<style lang="scss" scoped>
.app-chip {
  display: flex;
  align-items: center;
  padding: 0 16px;
  color: var(--ui-text-primary);
  user-select: none;
  background-color: var(--ui-grey);
  border-radius: 100px;
  transition-timing-function: ease;
  transition-duration: 0.3s;
  transition-property: background-color, color;

  &:hover {
    background-color: var(--ui-grey--hover);
  }

  @media (hover: none) {
    &:hover {
      background-color: var(--ui-grey);
    }
  }

  &--disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  &--active {
    color: var(--ui-white);
    pointer-events: auto;
    cursor: pointer;
    background-color: var(--backgroundColor, #ff5044);

    &:hover {
      background-color: var(--backgroundColor, #ff5044);
    }
  }

  /* Size */
  &--size-sm {
    height: 32px;
    font-size: 12px;
    line-height: 16px;
  }

  &--size-md {
    height: 40px;
    font-size: 14px;
    line-height: 18px;
  }

  /* Weight */
  &--weight-semi-bold {
    font-weight: 600;
  }

  &--weight-extra-bold {
    font-weight: 800;
  }
}
</style>
